import StorageManager from "@/features/storage";
import { createSerializableLocation, navigateToLogin } from "@/lib/routing";
import { AppRoute } from "@/types/routes";

import { type Token } from "./types";

export class MissingRefreshTokenError extends Error {
  constructor() {
    super("No refresh token available for the current session");
    this.name = "MissingRefreshTokenError";
  }
}

let refreshPromise: Promise<string> | null = null;

/**
 * Single-flight token refresh: refresh tokens are single-use and rotating, so
 * concurrent 401s must share one /auth/refresh call — a second call with the
 * same refresh token would be rejected and log the user out.
 */
export const refreshAccessToken = (): Promise<string> => {
  refreshPromise ??= doRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const doRefresh = async (): Promise<string> => {
  const tokenData = StorageManager.getTokenData();
  if (!tokenData?.refresh_token) {
    throw new MissingRefreshTokenError();
  }

  // Imported lazily: a static import would close the cycle
  // interceptors → token-refresh → generated client → axios-instance-v3 → interceptors.
  const { refreshTokensAuthRefreshPost } = await import(
    "@/api-v3/client/auth/auth"
  );

  const pair = await refreshTokensAuthRefreshPost(
    { refresh_token: tokenData.refresh_token },
    { skipGlobalErrorToast: true, skipAuthErrorLogout: true }
  );

  // Re-read so fields updated mid-flight (e.g. requiresPinChange) survive.
  const latest: Token = StorageManager.getTokenData() ?? tokenData;
  StorageManager.setTokenData({
    ...latest,
    access_token: pair.access_token,
    refresh_token: pair.refresh_token,
  });

  return pair.access_token;
};

/**
 * Terminal auth failure: clear the session and land on the login page with
 * the current location preserved, without waiting for a guard re-render.
 */
export const logoutToLogin = (): void => {
  if (window.location.pathname === String(AppRoute.LOGIN)) {
    // navigateToLogin would clobber state.from with /login itself.
    StorageManager.removeToken();
    return;
  }

  navigateToLogin(createSerializableLocation(window.location));
};
